import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface InvoiceLineForm {
  description: string;
  quantity: string;
  unit_price_tnd: string;
  vat_rate: 0 | 7 | 13 | 19;
  vat_exemption_code: string;
}

interface FacturierResult {
  invoice: {
    invoice_number: string;
    invoice_type: string;
    issue_date: string;
    due_date?: string;
    currency: string;
    seller_name: string;
    seller_matricule_fiscal: string;
    customer_name: string;
    customer_matricule_fiscal?: string;
    lines: Array<{
      line_number: number;
      description: string;
      quantity: number;
      unit_price_ht_millimes: number;
      vat_rate: number;
      vat_exemption_code?: string;
      amount_ht_millimes: number;
      amount_vat_millimes: number;
      amount_ttc_millimes: number;
    }>;
    amount_ht_millimes: number;
    amount_vat_millimes: number;
    amount_ttc_millimes: number;
    vat_breakdown_by_rate: Record<string, { ht: number; vat: number }>;
  };
  ttn: {
    ttn_reference: string | null;
    response_code: 'accepted' | 'rejected' | 'pending';
    status: string;
    submitted_at: string;
    findings: Array<{ severity: string; code: string; description: string }>;
  };
}

const EMPTY_LINE: InvoiceLineForm = {
  description: '',
  quantity: '1',
  unit_price_tnd: '0.000',
  vat_rate: 19,
  vat_exemption_code: '',
};

export function FacturierPage() {
  const [invoiceNumber, setInvoiceNumber] = useState('F-2026-001');
  const [invoiceType, setInvoiceType] = useState('sale');
  const [issueDate, setIssueDate] = useState('2026-05-04');
  const [dueDate, setDueDate] = useState('2026-06-04');
  const [sellerName, setSellerName] = useState('Sahel Textile Industries SARL');
  const [sellerMf, setSellerMf] = useState('1234567A/M/A/000');
  const [customerName, setCustomerName] = useState('Client ACME SARL');
  const [customerMf, setCustomerMf] = useState('5555555C/A/A/000');
  const [customerAddress, setCustomerAddress] = useState('15 avenue Bourguiba, Tunis');
  const [lines, setLines] = useState<InvoiceLineForm[]>([
    { description: 'Prestation conseil', quantity: '10', unit_price_tnd: '100.000', vat_rate: 19, vat_exemption_code: '' },
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function updateLine(idx: number, patch: Partial<InvoiceLineForm>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  }
  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        invoice_number: invoiceNumber,
        invoice_type: invoiceType,
        issue_date: issueDate,
        seller: { name: sellerName, matricule_fiscal: sellerMf },
        customer: {
          name: customerName,
          ...(customerMf.trim() ? { matricule_fiscal: customerMf.trim() } : {}),
          ...(customerAddress.trim() ? { address: customerAddress.trim() } : {}),
        },
        lines: lines.map((l) => ({
          description: l.description,
          quantity: parseFloat(l.quantity || '0'),
          unit_price_ht_millimes: tndStringToMillimes(l.unit_price_tnd),
          vat_rate: l.vat_rate,
          ...(l.vat_exemption_code.trim() ? { vat_exemption_code: l.vat_exemption_code.trim() } : {}),
        })),
      };
      if (dueDate) payload.due_date = dueDate;

      const res = await api.invokeAgent('facturier', {
        payload,
        fiscal_reference_date: issueDate,
      });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Facturier"
      module="Facturation · Phase 1"
      model="Sonnet 4.6"
      description={
        <>
          Émission de facture conforme TEIF — calcul TVA ligne par ligne avec exonérations,
          soumission TTN El Fatoora (mode fixture en bootstrap), tracking de la référence TTN.
          Tous les chiffres sortent de engine-tva, jamais du LLM.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="En-tête de facture">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="N° facture">
              <input className={inputCls + ' font-mono'} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} required />
            </Field>
            <Field label="Type">
              <select className={inputCls} value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}>
                <option value="sale">Vente</option>
                <option value="credit_note">Avoir</option>
                <option value="corrective">Rectificative</option>
                <option value="recurring">Récurrente</option>
              </select>
            </Field>
            <Field label="Date d'émission">
              <input type="date" className={inputCls} value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
            </Field>
            <Field label="Échéance">
              <input type="date" className={inputCls} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </div>
        </FormCard>

        <FormCard title="Émetteur">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Raison sociale">
              <input className={inputCls} value={sellerName} onChange={(e) => setSellerName(e.target.value)} required />
            </Field>
            <Field label="Matricule fiscal">
              <input className={inputCls + ' font-mono'} value={sellerMf} onChange={(e) => setSellerMf(e.target.value.toUpperCase())} required />
            </Field>
          </div>
        </FormCard>

        <FormCard title="Client">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nom">
              <input className={inputCls} value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
            </Field>
            <Field label="Matricule fiscal" hint="Optionnel — laisser vide pour B2C ; saisir 0000000Z/N/A/000 pour tester un rejet TTN">
              <input className={inputCls + ' font-mono'} value={customerMf} onChange={(e) => setCustomerMf(e.target.value.toUpperCase())} />
            </Field>
            <Field label="Adresse">
              <input className={inputCls} value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} />
            </Field>
          </div>
        </FormCard>

        <FormCard
          title={`Lignes de facturation (${lines.length})`}
          action={
            <button type="button" onClick={addLine} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">
              + Ajouter une ligne
            </button>
          }
        >
          <div className="space-y-3">
            {lines.map((l, idx) => (
              <div key={idx} className="border border-border rounded p-3 bg-cream/30">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <Field label="Description"><input className={inputCls} value={l.description} onChange={(e) => updateLine(idx, { description: e.target.value })} required /></Field>
                  <Field label="Qté"><input className={inputCls + ' font-mono'} value={l.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} /></Field>
                  <Field label="P.U. HT (TND)"><input className={inputCls + ' font-mono'} value={l.unit_price_tnd} onChange={(e) => updateLine(idx, { unit_price_tnd: e.target.value })} /></Field>
                  <Field label="TVA"><select className={inputCls} value={l.vat_rate} onChange={(e) => updateLine(idx, { vat_rate: parseInt(e.target.value, 10) as 0 | 7 | 13 | 19 })}>
                    <option value={19}>19%</option>
                    <option value={13}>13%</option>
                    <option value={7}>7%</option>
                    <option value={0}>0%</option>
                  </select></Field>
                  {l.vat_rate === 0 && (
                    <Field label="Code exonération" hint="ex. TVA_EXP_004 (export, Art. 4)">
                      <input className={inputCls + ' font-mono'} value={l.vat_exemption_code} onChange={(e) => updateLine(idx, { vat_exemption_code: e.target.value })} />
                    </Field>
                  )}
                  {lines.length > 1 && (
                    <button type="button" onClick={() => removeLine(idx)} className="text-xs text-red-700 hover:text-red-900 px-2">
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </FormCard>

        <button type="submit" disabled={loading || lines.length === 0} className="btn-primary">
          {loading ? 'Émission…' : 'Émettre la facture'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <FacturierResultView result={result as FacturierResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function FacturierResultView({ result }: { result: FacturierResult }) {
  return (
    <div className="space-y-5">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Soumission TTN El Fatoora</div>
        <div className={`p-3 rounded border ${
          result.ttn.response_code === 'accepted'
            ? 'border-emerald-300 bg-emerald-50'
            : result.ttn.response_code === 'rejected'
            ? 'border-red-300 bg-red-50'
            : 'border-amber-300 bg-amber-50'
        }`}>
          <div className="flex items-center gap-3">
            <span className="text-xs px-2 py-0.5 uppercase tracking-wider rounded bg-white border border-zinc-300">
              {result.ttn.response_code}
            </span>
            {result.ttn.ttn_reference && (
              <span className="font-mono text-sm">Réf TTN : <strong>{result.ttn.ttn_reference}</strong></span>
            )}
            <span className="text-xs text-taupe">soumis {result.ttn.submitted_at}</span>
          </div>
          {result.ttn.findings.length > 0 && (
            <ul className="mt-2 text-xs space-y-1">
              {result.ttn.findings.map((f, i) => (
                <li key={i}>
                  <span className="font-mono">{f.code}</span> · {f.description}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Totaux facture</div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Stat label="HT" value={formatMillimes(result.invoice.amount_ht_millimes)} />
          <Stat label="TVA" value={formatMillimes(result.invoice.amount_vat_millimes)} />
          <Stat label="TTC" value={formatMillimes(result.invoice.amount_ttc_millimes)} highlight />
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Lignes ({result.invoice.lines.length})</div>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Description</th>
                <th className="px-2 py-2 text-right">Qté</th>
                <th className="px-2 py-2 text-right">P.U. HT</th>
                <th className="px-2 py-2 text-right">HT</th>
                <th className="px-2 py-2 text-center">TVA</th>
                <th className="px-2 py-2 text-right">TTC</th>
              </tr>
            </thead>
            <tbody>
              {result.invoice.lines.map((l) => (
                <tr key={l.line_number} className="border-b border-border/50">
                  <td className="px-2 py-1.5 font-mono">{l.line_number}</td>
                  <td className="px-2 py-1.5">{l.description}{l.vat_exemption_code && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">{l.vat_exemption_code}</span>}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{l.quantity}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{formatMillimes(l.unit_price_ht_millimes, { withSymbol: false })}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{formatMillimes(l.amount_ht_millimes, { withSymbol: false })}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{l.vat_rate}%</td>
                  <td className="px-2 py-1.5 text-right font-mono">{formatMillimes(l.amount_ttc_millimes, { withSymbol: false })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Ventilation par taux</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
          {Object.entries(result.invoice.vat_breakdown_by_rate).map(([rate, b]) => (
            <div key={rate} className="p-2 rounded border border-border bg-white">
              <div className="text-xs uppercase tracking-wider text-taupe">Taux {rate}%</div>
              <div className="text-xs">HT : <span className="font-mono">{formatMillimes(b.ht, { withSymbol: false })}</span></div>
              <div className="text-xs">TVA : <span className="font-mono">{formatMillimes(b.vat, { withSymbol: false })}</span></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded border text-center ${highlight ? 'border-gold bg-gold/10' : 'border-border bg-white'}`}>
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className={`font-mono text-lg ${highlight ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}
